import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications, useNotificationCount } from '../hooks/useInvestQueries';
import AppLayout from '../components/layouts/AppLayout';
import ResponsiveNavbar from '../components/ResponsiveNavbar';
import { api } from '../App';
import { Bell, Check, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: notifications = [], isLoading } = useNotifications();
  const { data: unreadCount = 0 } = useNotificationCount();

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications-count']);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications-count']);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${notificationId}`);
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications-count']);
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      markAsRead(notification.notification_id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Format time ago
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Get icon color based on notification type
  const getTypeColor = (type) => {
    switch (type) {
      case 'commitment_approved': return 'text-emerald-400';
      case 'commitment_rejected': return 'text-red-400';
      case 'payout_available': return 'text-[#D4AF37]';
      default: return 'text-blue-400';
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen">
        <ResponsiveNavbar />
        
        <div className="container-responsive max-w-4xl mx-auto py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <button 
                onClick={() => navigate(-1)} 
                className="flex items-center gap-2 text-[#94A3B8] hover:text-white mb-2 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <h1 className="font-['Outfit'] text-3xl font-bold text-white flex items-center gap-3">
                Notifications
                {unreadCount > 0 && (
                  <span className="bg-[#D4AF37] text-[#0A1128] text-sm px-2 py-1 rounded-full">{unreadCount} new</span>
                )}
              </h1>
            </div>
            
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 rounded-lg hover:bg-[#D4AF37]/20 transition-all font-medium text-sm w-fit"
              >
                <Check className="w-4 h-4" />
                Mark all as read
              </button>
            )}
          </div>

          {/* List */}
          <div className="glass-card rounded-xl overflow-hidden min-h-[400px]">
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-[#94A3B8]">
                <div className="w-16 h-16 bg-[#334155]/20 rounded-full flex items-center justify-center mb-4">
                  <Bell className="w-8 h-8 opacity-50" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">No notifications</h3>
                <p>You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-[#334155]/50">
                {notifications.map((notification) => (
                  <div
                    key={notification.notification_id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`group p-6 hover:bg-[#1E293B]/50 transition-all cursor-pointer flex gap-4 ${
                      !notification.read ? 'bg-[#D4AF37]/5' : 'bg-transparent'
                    }`}
                  >
                    {/* Status Dot */}
                    <div className="mt-2 shrink-0">
                      {!notification.read ? (
                        <div className="w-3 h-3 rounded-full bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.5)]" />
                      ) : (
                        <div className="w-3 h-3 rounded-full bg-[#334155]" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className={`font-semibold text-lg mb-1 ${getTypeColor(notification.type)}`}>
                            {notification.title}
                          </h3>
                          <p className="text-[#E2E8F0] leading-relaxed">
                            {notification.message}
                          </p>
                        </div>
                        <span className="text-sm text-[#94A3B8] whitespace-nowrap shrink-0">
                          {timeAgo(notification.created_at)}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={(e) => deleteNotification(notification.notification_id, e)}
                      className="opacity-0 group-hover:opacity-100 p-2 hover:bg-red-500/10 text-[#64748B] hover:text-red-400 rounded-lg transition-all self-center"
                      title="Delete notification"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
